import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../ui/toast';
import { useGetCCAConfigQuery } from '../../../../../utils/api_service/ccaApi';
import { applyJob, getJobPost } from '../../../../../utils/api_service/JobPosting';
import {
  useGetPreQualVideoPresignMutation,
  useLazyGetPreQualConfigQuery,
  useLazyGetPreQualFormQuery,
  useSubmitPublicPreQualMutation,
} from '../../../../../utils/api_service/preQualApi';
import { getVideoFromStorage } from '../../../../../utils/api_service/storage_api';
import { setAppLocale, SUPPORTED_LOCALES, SupportedLocale } from '../../../../../i18n/locale';
import { RootState } from '../../../../../utils/redux_store/store';
import type {
  PreScreenAnswers,
  PreScreenQuestion,
} from '../../../../../utils/types/assessment_types';
import type { JobPostingData } from '../../../../../utils/types/JobOpenings';
import {
  buildFacebookUrl,
  buildJobPostUrl,
  buildLinkedInUrl,
  buildMailtoUrl,
  buildShareContent,
  buildWhatsAppUrl,
  buildXUrl,
  getAvailableContentLanguages,
  resolveContentLang,
} from '../functions';
import { calculateDaysLeftText, formatUnixDate } from '../../functions';
import type {
  IApplyResponse,
  IPreQualAnswerPayload,
  IPreQualApiQuestion,
  IPreQualLocation,
  IUseJobPostDetails,
} from '../interfaces';
import type { IJobPostApiResponse } from '../interfaces/jobPostDetails.interface';

type StoredAnswer = PreScreenAnswers & {
  videoFile?: File;
  locationData?: IPreQualLocation;
};

const toApiQuestion = (question: IPreQualApiQuestion, index: number): PreScreenQuestion => ({
  questionId: question.questionKey || `temp-key-${index}-${Date.now()}`,
  question: question.question ?? '',
  questionType:
    question.questionType === 'number'
      ? 'numeric'
      : question.questionType === 'video-answer'
        ? 'video'
        : (question.questionType as PreScreenQuestion['questionType']),
  choices: question.choices,
});

const isHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const toAnswerPayload = (ans: StoredAnswer): IPreQualAnswerPayload => {
  const location = ans.locationData;
  return {
    questionKey: ans.questionId,
    value: String(ans.answer ?? ''),
    location: location
      ? {
        city: location.city,
        state: location.state,
        country: location.country,
        postcode: location.postcode,
        lat: location.coordinates?.coordinates[1] || 0,
        lng: location.coordinates?.coordinates[0] || 0,
        unknown: false,
      }
      : { city: '', state: '', country: '', postcode: '', lat: 0, lng: 0, unknown: true },
  };
};

export const useJobPostDetails = (): IUseJobPostDetails => {
  const [loading, setLoading] = useState<boolean>(false);
  const [jobPostData, setJobPostData] = useState<JobPostingData | undefined>(undefined);
  const [contentLang, setContentLangState] = useState<string>('en');
  const { i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File[]>>({});
  const [jobId, setJobId] = useState<string>('');
  const [jobPostId, setJobPostId] = useState<string>('');
  const [jobPostUrl, setJobPostUrl] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isQualifyingModalOpen, setIsQualifyingModalOpen] = useState<boolean>(false);
  const [isQualifyingCancelDialogOpen, setIsQualifyingCancelDialogOpen] = useState<boolean>(false);
  const [currentQualifyingStep, setCurrentQualifyingStep] = useState<number>(0);
  const [qualifyingAnswers, setQualifyingAnswers] = useState<PreScreenAnswers[]>([]);
  const [jobApplicantId, setJobApplicantId] = useState<string | null>(null);

  const [submitPublicPreQual] = useSubmitPublicPreQualMutation();
  const [getVideoPresign] = useGetPreQualVideoPresignMutation();
  const { data: ccaConfig } = useGetCCAConfigQuery(jobId, { skip: !jobId });
  const ccaEnabled =
    !!ccaConfig &&
    (ccaConfig.enabled === true ||
      (ccaConfig.questions?.length ?? 0) > 0 ||
      (ccaConfig.quickConfig?.subdomains?.length ?? 0) > 0);

  const [triggerPreQual, { data: preQualConfig }] = useLazyGetPreQualConfigQuery();
  const [triggerPreQualForm, { data: preQualForm }] = useLazyGetPreQualFormQuery();

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName) ?? '';

  const shareContent = buildShareContent(jobPostData?.jobTitle ?? '', companyName, jobPostUrl);
  const contentLanguages = getAvailableContentLanguages(jobPostData);

  useEffect(() => {
    if (jobId) {
      void triggerPreQual(jobId);
    }
  }, [id, jobId, triggerPreQual]);

  const qualifyingQuestions: PreScreenQuestion[] = useMemo(() => {
    const rawQuestions: IPreQualApiQuestion[] =
      preQualForm?.questions ?? preQualConfig?.questions ?? [];
    return rawQuestions.map((question, index) => toApiQuestion(question, index));
  }, [preQualConfig, preQualForm]);

  useEffect(() => {
    const fetchJobPost = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await getJobPost(id, contentLang);
        const responseData = (await response.json()) as IJobPostApiResponse;

        const jobPosting = (responseData.jobPosting ?? responseData) as JobPostingData;

        const documentRequirements = (
          responseData.documentRequirements ??
          jobPosting.documentRequirements ??
          []
        ).map((req) => ({ ...req, key: req.key }));

        let documentConfigurations =
          responseData.documentConfigurations ?? jobPosting.documentConfigurations ?? [];

        if (documentConfigurations.length > 0) {
          documentConfigurations = documentConfigurations.map((config) => ({
            ...config,
            type: config.type,
          }));
        }

        const extractedJobOpeningId =
          responseData.jobOpeningId ??
          jobPosting.jobOpeningId ??
          responseData.job_opening_id;

        setJobId(extractedJobOpeningId || responseData.id || jobPosting.id);
        setJobPostId(responseData.id || jobPosting.id);
        setJobPostData({
          ...jobPosting,
          documentRequirements,
          documentConfigurations,
        });

        if (jobPosting.id) {
          setJobPostUrl(buildJobPostUrl(window.location.origin, jobPosting.id));
        }
        if (jobPosting.videoFilename && typeof jobPosting.videoFilename === 'string') {
          try {
            if (isHttpUrl(jobPosting.videoFilename)) {
              setUrl(jobPosting.videoFilename);
            } else {
              const videoUrl = await getVideoFromStorage(jobPosting.videoFilename);
              setUrl(videoUrl);
            }
          } catch (err) {
            console.error('Error fetching video from storage:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching job post:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      void fetchJobPost();
    }
  }, [id, contentLang]);

  useEffect(() => {
    const target = resolveContentLang(jobPostData?.availableLanguages, i18n.language);
    if (target && target !== contentLang) {
      setContentLangState(target);
      if (SUPPORTED_LOCALES.includes(target as SupportedLocale)) {
        setAppLocale(target as SupportedLocale);
      }
    }
    // contentLang intentionally omitted to avoid a refetch loop
  }, [jobPostData?.id, i18n.language]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handlePlay = (): void => setIsPlaying(true);
      const handlePause = (): void => setIsPlaying(false);
      const handleEnded = (): void => setIsPlaying(false);

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handleEnded);

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
      };
    }
    return undefined;
  }, [url]);

  const handleSubmit = async (): Promise<void> => {
    const docsToSubmit = Object.entries(selectedFiles).flatMap(([key, files]) =>
      files.map((file) => ({ file, type: key })),
    );

    if (docsToSubmit.length > 0) {
      setLoading(true);
      try {
        const response = await applyJob(jobPostId, docsToSubmit);
        if (response.ok) {
          let responseData: IApplyResponse = {};
          try {
            responseData = (await response.json()) as IApplyResponse;
          } catch (e) {
            console.warn('Could not parse response JSON:', e);
          }

          const applicantId = responseData.job_applicant_id || responseData.id;
          if (applicantId) {
            setJobApplicantId(applicantId);
          }

          setIsModalOpen(false);
          setSelectedFiles({});

          let freshQuestions: IPreQualApiQuestion[] = [];
          try {
            if (jobId) {
              const result = (await triggerPreQualForm(jobId)) as {
                data?: { questions?: IPreQualApiQuestion[] };
              };
              freshQuestions = result?.data?.questions || [];
            }

            if (freshQuestions.length === 0 && jobId) {
              const result = (await triggerPreQual(jobId)) as {
                data?: { questions?: IPreQualApiQuestion[] };
              };
              freshQuestions = result?.data?.questions || [];
            }
          } catch (err) {
            console.error('Error fetching questions after upload:', err);
          }

          if (freshQuestions.length > 0) {
            setIsQualifyingModalOpen(true);
            setCurrentQualifyingStep(0);
            toast.success('Resume uploaded! Please answer these quick questions.', {
              position: 'bottom-center',
            });
          } else {
            toast.success('Your application has been submitted!', {
              position: 'bottom-center',
            });
          }
        } else {
          try {
            await response.json();
          } catch (e) {
            console.warn('Could not parse error response JSON:', e);
          }
          toast.error('Upload unsuccessful. Please try again.', {
            position: 'bottom-center',
          });
        }
      } catch (error) {
        console.error('Submission error:', error);
        toast.error('Error. Please try again.', {
          position: 'bottom-center',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    setSelectedFiles({});
  };

  const handleApply = (): void => {
    setIsDialogOpen(false);
    setIsModalOpen(true);
  };

  const handleQualifyingComplete = async (): Promise<void> => {
    if (currentQualifyingStep < qualifyingQuestions.length - 1) {
      setCurrentQualifyingStep((prev) => prev + 1);
    } else if (jobApplicantId) {
      setLoading(true);
      try {
        const formattedAnswers = await Promise.all(
          (qualifyingAnswers as StoredAnswer[]).map(async (ans) => {
            const videoFile = ans.videoFile;
            let videoKey: string | undefined;

            if (videoFile) {
              const presign = await getVideoPresign({
                questionKey: ans.questionId,
                jobApplicantId,
              }).unwrap();
              const typelessBlob = new Blob([videoFile]);
              await fetch(presign.url, {
                method: 'PUT',
                body: typelessBlob,
              });
              videoKey = presign.videoKey || presign.url;
            }

            const payload = toAnswerPayload(ans);
            return { ...payload, value: videoKey ? '' : payload.value, videoKey };
          }),
        );

        await submitPublicPreQual({
          jobApplicantId,
          answers: formattedAnswers,
        }).unwrap();

        toast.success('Application complete! Thank you.', {
          position: 'bottom-center',
        });
      } catch (error) {
        console.error('Error submitting pre-qual:', error);
        toast.error('Failed to submit supplementary info, but your resume was received.', {
          position: 'bottom-center',
        });
      } finally {
        setLoading(false);
      }
      setIsQualifyingModalOpen(false);
    } else {
      setIsQualifyingModalOpen(false);
    }
  };

  const getAllowMultiple = (type: string): boolean => {
    if (jobPostData?.documentRequirements) {
      const req = jobPostData.documentRequirements.find((entry) => (entry.key || '') === type);
      return req?.allowMultiple ?? false;
    }
    return false;
  };

  const handleFileSelect = (typeOrKey: string, file: File | null): void => {
    setSelectedFiles((prev) => {
      const newFiles = { ...prev };
      if (file) {
        const canMultiple = getAllowMultiple(typeOrKey);
        if (canMultiple) {
          newFiles[typeOrKey] = [...(prev[typeOrKey] || []), file];
        } else {
          newFiles[typeOrKey] = [file];
        }
      } else {
        delete newFiles[typeOrKey];
      }
      return newFiles;
    });
  };

  const handleFileRemoveAtIndex = (type: string, index: number): void => {
    setSelectedFiles((prev) => {
      const existing = prev[type] || [];
      const updated = existing.filter((_, i) => i !== index);
      if (updated.length === 0) {
        const { [type]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [type]: updated };
    });
  };

  const handleBatchFileSelect = (updates: Record<string, File | null>): void => {
    setSelectedFiles((prev) => {
      const newFiles = { ...prev };
      Object.entries(updates).forEach(([type, file]) => {
        if (file) {
          const canMultiple = getAllowMultiple(type);
          if (canMultiple) {
            newFiles[type] = [...(prev[type] || []), file];
          } else {
            newFiles[type] = [file];
          }
        } else {
          delete newFiles[type];
        }
      });
      return newFiles;
    });
  };

  const shareOnWhatsApp = (): void => {
    window.open(buildWhatsAppUrl(shareContent.shareText), '_blank');
    toast.success('WhatsApp opened!', { position: 'bottom-center' });
  };

  const shareOnFacebook = (): void => {
    window.open(buildFacebookUrl(shareContent.shareText, jobPostUrl), '_blank');
    toast.success('Facebook opened!', { position: 'bottom-center' });
  };

  const shareOnLinkedIn = (): void => {
    window.open(buildLinkedInUrl(shareContent.emailSubject, shareContent.shareText, jobPostUrl), '_blank');
    toast.success('LinkedIn opened!', { position: 'bottom-center' });
  };

  const shareOnX = (): void => {
    window.open(buildXUrl(shareContent.shareText, jobPostUrl), '_blank');
    toast.success('X (Twitter) opened!', { position: 'bottom-center' });
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      if (!jobPostUrl) {
        toast.error('Job post URL not available', {
          position: 'bottom-center',
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(jobPostUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = jobPostUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      toast.success('Job URL copied to clipboard!', {
        position: 'bottom-center',
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard', {
        position: 'bottom-center',
      });
    }
  };

  const handlePlayPause = (): void => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        void videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const isMandatoryMissing = useMemo(() => {
    if (!jobPostData) return true;

    if (jobPostData.documentRequirements && jobPostData.documentRequirements.length > 0) {
      return jobPostData.documentRequirements.some(
        (req) => req.isMandatory && !(selectedFiles[req.key]?.length > 0),
      );
    }

    if (jobPostData.documentConfigurations && jobPostData.documentConfigurations.length > 0) {
      return jobPostData.documentConfigurations.some(
        (conf) => conf.requirement === 'Mandatory' && !(selectedFiles[conf.type]?.length > 0),
      );
    }

    return Object.keys(selectedFiles).length === 0;
  }, [jobPostData, selectedFiles]);

  const setContentLang = (lang: string): void => {
    setContentLangState(lang);
    if (SUPPORTED_LOCALES.includes(lang as SupportedLocale)) {
      setAppLocale(lang as SupportedLocale);
    }
  };

  return {
    jobPostData,
    loading,
    isDialogOpen,
    isModalOpen,
    selectedFiles,
    url,
    isPlaying,
    videoRef,
    handlePlayPause,
    handleApply,
    handleFileSelect,
    handleFileRemoveAtIndex,
    handleBatchFileSelect,
    handleSubmit,
    handleCloseModal,
    copyToClipboard,
    shareOnLinkedIn,
    shareOnWhatsApp,
    shareOnFacebook,
    shareOnX,
    formatUnixTimestamp: formatUnixDate,
    calculateDaysLeft: () => calculateDaysLeftText(jobPostData?.endDate),
    companyLogo,
    mailtoUrl:
      jobPostId && jobPostUrl ? buildMailtoUrl(shareContent.emailSubject, shareContent.emailBody) : '',
    setIsDialogOpen,
    isQualifyingModalOpen,
    setIsQualifyingModalOpen,
    isQualifyingCancelDialogOpen,
    setIsQualifyingCancelDialogOpen,
    currentQualifyingStep,
    setCurrentQualifyingStep,
    qualifyingQuestions,
    qualifyingAnswers,
    setQualifyingAnswers,
    handleQualifyingComplete,
    isFinalQualifyingStep: currentQualifyingStep === qualifyingQuestions.length - 1,
    canSubmit: !isMandatoryMissing && !loading,
    ccaEnabled,
    companyName,
    contentLang,
    contentLanguages,
    setContentLang,
  };
};
